/**
 * Firestore service for user's card collection
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { auth, firestore } from '@/services/firebase';
import { bumpFolderUpdatedAt, bumpFolderUpdatedAtInBatch, getFolderPath, getFolders } from '@/services/folders';
import type { CollectionCard, EnrichedCollectionCard } from '@/types/card';
import type { CreateCollectionCard } from '@/types/schemas';
import { getCardsByIds } from './cards';

function getUserCollectionRef(userId: string) {
  return collection(firestore, 'users', userId, 'collection');
}

function getCurrentUserId(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
}

/**
 * Get all cards in user's collection
 */
export async function getCollectionCards(): Promise<CollectionCard[]> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  const q = query(collectionRef, orderBy('created_at', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(
    (docSnap) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as CollectionCard,
  );
}

/**
 * Get enriched collection cards (with full card data and folder paths)
 */
export async function getEnrichedCollectionCards(): Promise<EnrichedCollectionCard[]> {
  const [collectionCards, folders] = await Promise.all([getCollectionCards(), getFolders()]);

  if (collectionCards.length === 0) {
    return [];
  }

  // Get unique Scryfall IDs
  const scryfallIds = [...new Set(collectionCards.map((c) => c.scryfall_id))];
  const cardsMap = await getCardsByIds(scryfallIds);

  // Enrich collection cards with card data and folder path
  return collectionCards
    .map((collectionCard) => {
      const card = cardsMap.get(collectionCard.scryfall_id);
      if (!card) {
        console.warn(`Card not found for scryfall_id: ${collectionCard.scryfall_id}`);
        return null;
      }
      return {
        ...collectionCard,
        card,
        folder_path: getFolderPath(collectionCard.folder_id, folders),
      };
    })
    .filter((c): c is EnrichedCollectionCard => c !== null);
}

/**
 * Get collection cards in a specific folder
 */
export async function getCollectionCardsByFolder(folderId: string | null): Promise<CollectionCard[]> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);

  const q =
    folderId === null
      ? query(collectionRef, where('folder_id', '==', null), orderBy('created_at', 'desc'))
      : query(collectionRef, where('folder_id', '==', folderId), orderBy('created_at', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docSnap) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as CollectionCard,
  );
}

/**
 * Get a single collection card by ID
 */
export async function getCollectionCardById(cardId: string): Promise<CollectionCard | null> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'collection', cardId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as CollectionCard;
}

/**
 * Add a card to the collection
 */
export async function addCollectionCard(data: CreateCollectionCard): Promise<string> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);

  const docData = {
    ...data,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const docRef = await addDoc(collectionRef, docData);

  // Bump folder updated_at so "Updated" sort reflects activity
  if (data.folder_id) {
    bumpFolderUpdatedAt(data.folder_id).catch(console.error);
  }

  return docRef.id;
}

/**
 * Add multiple cards to the collection (batch).
 *
 * With `persistentLocalCache` enabled, `batch.commit()` waits for server
 * acknowledgment which can hang on slow or offline connections. Because local
 * persistence guarantees data safety (and `onSnapshot` fires immediately with
 * the local write), we fire the commit without blocking so the caller isn't
 * stuck waiting for a server round-trip.
 *
 * Batches are committed 2 at a time to avoid overwhelming Firestore and to
 * let the UI show incremental progress via `onBatchCommitted`.
 */
export async function addCollectionCards(
  cards: CreateCollectionCard[],
  onBatchCommitted?: (committedSoFar: number) => void,
): Promise<string[]> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  const ids: string[] = [];

  // Firestore batch limit is 500 operations
  const batchSize = 500;
  const batches: { batch: ReturnType<typeof writeBatch>; count: number }[] = [];

  for (let i = 0; i < cards.length; i += batchSize) {
    const batch = writeBatch(firestore);
    const chunk = cards.slice(i, i + batchSize);

    for (const card of chunk) {
      const docRef = doc(collectionRef);
      ids.push(docRef.id);
      batch.set(docRef, {
        ...card,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
    }

    batches.push({ batch, count: chunk.length });
  }

  // Commit batches with concurrency limit of 2.
  // commit() writes to local persistence immediately; the returned promise
  // waits for server ack which can be slow. We don't await server ack — just
  // fire and report progress via the callback.
  const CONCURRENCY = 2;
  let committedSoFar = 0;
  const pendingCommits: Promise<void>[] = [];

  for (let i = 0; i < batches.length; i += CONCURRENCY) {
    const window = batches.slice(i, i + CONCURRENCY);
    const commits = window.map(({ batch, count }) => {
      const promise = batch.commit();
      // Track progress from local write (fires before server ack)
      committedSoFar += count;
      const snapshot = committedSoFar;
      onBatchCommitted?.(snapshot);
      return promise;
    });
    pendingCommits.push(...commits);
    // Yield to the event loop between windows so onSnapshot / UI can update
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  // Let server sync settle in the background — don't block the caller.
  Promise.allSettled(pendingCommits).then((results) => {
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      console.error(`[Collection] ${failed.length}/${results.length} batch commits failed:`, failed);
    }
  });

  // Bump updated_at for all affected folders
  const affectedFolderIds = new Set(cards.map((c) => c.folder_id).filter((id): id is string => !!id));
  for (const folderId of affectedFolderIds) {
    bumpFolderUpdatedAt(folderId).catch(console.error);
  }

  return ids;
}

/**
 * Update a collection card's quantity
 */
export async function updateCardQuantity(cardId: string, quantity: number): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'collection', cardId);

  // Read folder_id before potential deletion
  const cardDoc = await getDoc(docRef);
  const folderId = cardDoc.data()?.folder_id;

  if (quantity <= 0) {
    await deleteDoc(docRef);
  } else {
    await updateDoc(docRef, {
      quantity,
      updated_at: serverTimestamp(),
    });
  }

  if (folderId) {
    bumpFolderUpdatedAt(folderId).catch(console.error);
  }
}

/**
 * Update a collection card
 */
export async function updateCollectionCard(
  cardId: string,
  data: Partial<Omit<CollectionCard, 'id' | 'created_at' | 'updated_at'>>,
): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'collection', cardId);

  // Read current folder_id before update (folder may be changing)
  const cardDoc = await getDoc(docRef);
  const oldFolderId = cardDoc.data()?.folder_id;
  const newFolderId = data.folder_id;

  await updateDoc(docRef, {
    ...data,
    updated_at: serverTimestamp(),
  });

  // Bump affected folders
  const foldersToBump = new Set<string>();
  if (oldFolderId) foldersToBump.add(oldFolderId);
  if (newFolderId) foldersToBump.add(newFolderId);
  for (const fId of foldersToBump) {
    bumpFolderUpdatedAt(fId).catch(console.error);
  }
}

/**
 * Delete a collection card
 */
export async function deleteCollectionCard(cardId: string): Promise<void> {
  const userId = getCurrentUserId();
  const docRef = doc(firestore, 'users', userId, 'collection', cardId);
  const cardDoc = await getDoc(docRef);
  const cardFolderId = cardDoc.data()?.folder_id;
  await deleteDoc(docRef);
  if (cardFolderId) {
    await bumpFolderUpdatedAt(cardFolderId);
  }
}

/**
 * Move cards to a folder (splits at 500 docs per Firestore batch limit)
 */
export async function moveCardsToFolder(
  cardIds: string[],
  folderId: string | null,
  onProgress?: (completedSoFar: number) => void,
): Promise<void> {
  const userId = getCurrentUserId();
  const BATCH_SIZE = 500;

  // Collect source folder IDs from the cards being moved
  const sourceFolderIds = new Set<string>();
  for (const cardId of cardIds) {
    const cardDoc = await getDoc(doc(firestore, 'users', userId, 'collection', cardId));
    const cardFolderId = cardDoc.data()?.folder_id;
    if (cardFolderId && cardFolderId !== folderId) {
      sourceFolderIds.add(cardFolderId);
    }
  }

  for (let i = 0; i < cardIds.length; i += BATCH_SIZE) {
    const chunk = cardIds.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(firestore);

    for (const cardId of chunk) {
      const docRef = doc(firestore, 'users', userId, 'collection', cardId);
      batch.update(docRef, {
        folder_id: folderId,
        updated_at: serverTimestamp(),
      });
    }

    // Bump target folder in the last batch
    if (folderId && i + BATCH_SIZE >= cardIds.length) {
      bumpFolderUpdatedAtInBatch(batch, doc(firestore, 'users', userId, 'folders', folderId));
    }

    await batch.commit();
    onProgress?.(i + chunk.length);
  }

  // Bump source folders
  for (const srcId of sourceFolderIds) {
    await bumpFolderUpdatedAt(srcId);
  }
}

/**
 * Batch update collection cards with a partial patch.
 * Splits at 500 docs per Firestore batch limit.
 */
export async function batchUpdateCollectionCards(
  cardIds: string[],
  patch: Record<string, unknown>,
  onProgress?: (completedSoFar: number) => void,
): Promise<void> {
  const userId = getCurrentUserId();
  const BATCH_SIZE = 500;

  // Collect affected folder IDs from the cards being updated
  const affectedFolderIds = new Set<string>();
  for (const cardId of cardIds) {
    const cardDoc = await getDoc(doc(firestore, 'users', userId, 'collection', cardId));
    const fId = cardDoc.data()?.folder_id;
    if (fId) affectedFolderIds.add(fId);
  }

  for (let i = 0; i < cardIds.length; i += BATCH_SIZE) {
    const chunk = cardIds.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(firestore);

    for (const cardId of chunk) {
      const docRef = doc(firestore, 'users', userId, 'collection', cardId);
      batch.update(docRef, {
        ...patch,
        updated_at: serverTimestamp(),
      });
    }

    await batch.commit();
    onProgress?.(i + chunk.length);
  }

  // If the patch includes a folder_id change, also bump the new folder
  if (typeof patch.folder_id === 'string') {
    affectedFolderIds.add(patch.folder_id);
  }

  for (const fId of affectedFolderIds) {
    bumpFolderUpdatedAt(fId).catch(console.error);
  }
}

/**
 * Batch delete collection cards.
 * Splits at 500 docs per Firestore batch limit.
 */
export async function batchDeleteCollectionCards(cardIds: string[], onProgress?: (completedSoFar: number) => void): Promise<void> {
  const userId = getCurrentUserId();
  const BATCH_SIZE = 500;

  // Collect affected folder IDs before deletion
  const affectedFolderIds = new Set<string>();
  for (const cardId of cardIds) {
    const cardDoc = await getDoc(doc(firestore, 'users', userId, 'collection', cardId));
    const fId = cardDoc.data()?.folder_id;
    if (fId) affectedFolderIds.add(fId);
  }

  for (let i = 0; i < cardIds.length; i += BATCH_SIZE) {
    const chunk = cardIds.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(firestore);

    for (const cardId of chunk) {
      const docRef = doc(firestore, 'users', userId, 'collection', cardId);
      batch.delete(docRef);
    }

    await batch.commit();
    onProgress?.(i + chunk.length);
  }

  // Bump affected folders
  for (const fId of affectedFolderIds) {
    await bumpFolderUpdatedAt(fId);
  }
}

/**
 * Delete cards by source system (for re-import)
 */
export async function deleteCardsBySource(sourceSystem: string): Promise<number> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  const q = query(collectionRef, where('source_system', '==', sourceSystem));
  const snapshot = await getDocs(q);

  // Collect affected folder IDs before deletion
  const affectedFolderIds = new Set<string>();
  snapshot.docs.forEach((docSnap) => {
    const fId = docSnap.data().folder_id;
    if (fId) affectedFolderIds.add(fId);
  });

  const batchSize = 500;
  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = writeBatch(firestore);
    const chunk = docs.slice(i, i + batchSize);
    for (const docSnap of chunk) {
      batch.delete(docSnap.ref);
    }
    await batch.commit();
  }

  for (const fId of affectedFolderIds) {
    bumpFolderUpdatedAt(fId).catch(console.error);
  }

  return snapshot.size;
}

/**
 * Count cards by source system (to check if re-import needed)
 */
export async function getCountBySource(sourceSystem: string): Promise<number> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  const q = query(collectionRef, where('source_system', '==', sourceSystem));
  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Delete cards by folder (for folder-based overwrite during import)
 */
export async function deleteCardsByFolder(folderId: string): Promise<number> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  const q = query(collectionRef, where('folder_id', '==', folderId));
  const snapshot = await getDocs(q);

  if (snapshot.empty) return 0;

  const batchSize = 500;
  const docs = snapshot.docs;
  let deletedCount = 0;

  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = writeBatch(firestore);
    const chunk = docs.slice(i, i + batchSize);
    for (const docSnap of chunk) {
      batch.delete(docSnap.ref);
    }
    await batch.commit();
    deletedCount += chunk.length;
  }

  bumpFolderUpdatedAt(folderId).catch(console.error);

  return deletedCount;
}

/**
 * Count cards in a specific folder
 */
export async function getCountByFolder(folderId: string): Promise<number> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  const q = query(collectionRef, where('folder_id', '==', folderId));
  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Delete ALL cards in user's collection
 * Used for testing/development - clears entire collection
 */
export async function clearAllCollectionCards(onProgress?: (cleared: number, total: number) => void): Promise<number> {
  const userId = getCurrentUserId();
  const collectionRef = getUserCollectionRef(userId);
  console.info('[clear collection] Fetching all cards for user:', userId);
  const snapshot = await getDocs(collectionRef);
  console.info(`[clear collection] Found ${snapshot.size} cards to delete for user:`, userId);

  if (snapshot.empty) return 0;

  const batchSize = 500;
  const docs = snapshot.docs;
  const total = docs.length;
  let deletedCount = 0;

  onProgress?.(0, total);

  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = writeBatch(firestore);
    const chunk = docs.slice(i, i + batchSize);

    for (const docSnap of chunk) {
      batch.delete(docSnap.ref);
    }

    await batch.commit();
    deletedCount += chunk.length;
    onProgress?.(deletedCount, total);
  }

  return deletedCount;
}

/**
 * Update multiple collection cards' quantities (batch)
 * Used for "Update existing" import mode
 *
 * @param updates - Map of card ID to new quantity
 * @returns Number of cards updated
 */
export async function updateCollectionCardsQuantity(updates: Map<string, number>): Promise<number> {
  const userId = getCurrentUserId();
  let updatedCount = 0;

  // Convert map to array for batching
  const updateEntries = Array.from(updates.entries());

  // Collect affected folder IDs
  const affectedFolderIds = new Set<string>();
  for (const [cardId] of updateEntries) {
    const cardDoc = await getDoc(doc(firestore, 'users', userId, 'collection', cardId));
    const fId = cardDoc.data()?.folder_id;
    if (fId) affectedFolderIds.add(fId);
  }

  // Firestore batch limit is 500 operations
  const batchSize = 500;
  for (let i = 0; i < updateEntries.length; i += batchSize) {
    const batch = writeBatch(firestore);
    const chunk = updateEntries.slice(i, i + batchSize);

    for (const [cardId, quantity] of chunk) {
      const docRef = doc(firestore, 'users', userId, 'collection', cardId);
      if (quantity <= 0) {
        batch.delete(docRef);
      } else {
        batch.update(docRef, {
          quantity,
          updated_at: serverTimestamp(),
        });
      }
    }

    await batch.commit();
    updatedCount += chunk.length;
  }

  for (const fId of affectedFolderIds) {
    bumpFolderUpdatedAt(fId).catch(console.error);
  }

  return updatedCount;
}
